import { isRejectedWithValue } from "@reduxjs/toolkit";
import { addError, removeError } from "state/slices/auth/ErrorSlice";
import { setSessionExpired } from "state/slices/sessionSlice";
import { ErrorStatusCodeMapper } from "utils/ErrorStatusCodeMapper";
import { msalLogout } from "utils/msalLogout";
import { showToast } from "utils/reusableToast";

/**
 * Check for errors and surfaces them as a toast (see utils/reusableToast).
 * Also mirrors into ErrorSlice for now — nothing currently reads state.error
 * (the old <Errors/> banner has been removed from Layout/AdminLayout), kept
 * only so this isn't a breaking change for any other in-flight work.
 */
export const rtkQueryErrorLogger = (store) => (next) => (action) => {
  // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
  const conditionError = "ConditionError";
  /**If we got actual rejection from the APIs than store error in error slice */

  if (
    isRejectedWithValue(action) ||
    (action.type.endsWith("/rejected") && action.error.name !== conditionError)
  ) {
    let message =
      action?.payload?.data?.error_message || action?.payload?.data?.error;

    if (message === undefined) {
      message = action?.payload?.data?.detail;
    }

    // The backend doesn't consistently use 401 for an expired/invalid token —
    // it can come back as 403 with { error_code: "INVALID_TOKEN" } in the
    // body instead, which the old status-only check below would never catch.
    const isInvalidToken =
      action?.payload?.status === 401 ||
      action?.payload?.data?.error_code === "INVALID_TOKEN";

    // If the error is 401 (or an INVALID_TOKEN error_code), auth token is
    // expired/invalid, so we need to log the user out.
    if (isInvalidToken || message === undefined) {
      if (isInvalidToken) {
        store.dispatch(setSessionExpired(true));
      }
      message = ErrorStatusCodeMapper(action.meta?.arg?.endpointName)[
        action?.payload?.status
      ];
    }

    // If the error is 404, we don't want to show the error message
    if (action?.payload?.data?.error_code === 404) {
      return next(action);
    }

    // Once a session-expiry is already known (this failure or an earlier
    // one), InactiveLogout's single "Session Expired" dialog is already
    // about to show/has shown — a burst of other in-flight requests failing
    // for the same reason around the same time must not each pile on their
    // own "Something went wrong" toast on top of it.
    if (isInvalidToken || store.getState().session.sessionExpired) {
      return next(action);
    }

    const endpointName = action.meta?.arg?.endpointName;

    store.dispatch(
      addError({
        url: endpointName,
        message: message,
        status: action?.payload?.status,
      })
    );

    showToast({
      title: "Something went wrong",
      description: message,
      type: "error",
      id: `api-error-${endpointName}`,
    });

    setTimeout(() => {
      store.dispatch(removeError(endpointName));
    }, 60000);
  }

  return next(action);
};
